/**
 * DCMS Discovery Module (TypeScript)
 * Handles DCMS-based device discovery functionality with type safety
 * Sprint 31: Updated for environment-aware communication
 *
 * This module is critical for the VC demo - it handles real device discovery
 * from AWS EC2 instances via the Discovery Hub at unboundata.com:8080
 */

import { ValidationUtils } from '../utils/validation';
import { environment } from '../config/environment';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * DiscoveryLogger interface based on discovery-logging.js
 */
interface DiscoveryLogger {
    logElement: HTMLElement | null;
    logContainer: HTMLElement | null;
    log(message: string, type?: 'error' | 'warning' | 'success' | 'phase' | 'progress' | 'detail' | 'info'): void;
    logError(message: string): void;
    logWarning(message: string): void;
    logSuccess(message: string): void;
    logPhase(message: string): void;
    logProgress(message: string): void;
    logDetail(message: string): void;
    logInfo(message: string): void;
    logSpacer(): void;
    startSession(sessionName: string): void;
    endSession(sessionName: string, success?: boolean): void;
    show(): void;
    hide(): void;
    clear(): void;
    scrollToBottom(): void;
}

/**
 * Discovery configuration from UI and config manager
 */
interface DiscoveryConfig {
    discoveryHub: {
        ip: string;
        port: number;
        endpoint: string;
    };
    discoveryMethod: string;
    credentials: string;
    detailedLogging: boolean;
    generateSpreadsheet: boolean;
}

/**
 * Parsed target input result with IPs and type
 */
interface ParsedTargetInput {
    targetIPs: string[];
    discoveryType: 'single' | 'cidr' | 'range' | 'multiple';
}

/**
 * Discovery status types (Graduated Discovery Levels)
 * @see GRADUATED_DISCOVERY_LEVELS.md for full documentation
 */
export type DiscoveryStatus =
    | 'full'          // Full discovery: hostname, OS, configs
    | 'partial'       // Partial discovery: hostname/OS but no configs
    | 'port_scan'     // Ports detected only (no SSH access)
    | 'auth_failed'   // SSH available but authentication failed
    | 'offline'       // No network response
    | 'discovered'    // Legacy status for backward compatibility
    | 'failed';       // Generic failure

/**
 * Discovery Status Classification
 * Provides semantic grouping of discovery statuses
 */
export class DiscoveryStatusClassifier {
    /**
     * Statuses that indicate successful data collection
     * These devices should be stored in vector_db
     */
    static readonly SUCCESSFUL_STATUSES: ReadonlySet<DiscoveryStatus> = new Set([
        'full',
        'partial',
        'discovered' // Legacy status
    ]);

    /**
     * Statuses that indicate device is reachable but needs attention
     */
    static readonly ACTIONABLE_STATUSES: ReadonlySet<DiscoveryStatus> = new Set([
        'port_scan',
        'auth_failed'
    ]);

    /**
     * Statuses that indicate device is not reachable
     */
    static readonly UNREACHABLE_STATUSES: ReadonlySet<DiscoveryStatus> = new Set([
        'offline',
        'failed'
    ]);

    /**
     * Check if status indicates successful discovery
     */
    static isSuccessful(status: DiscoveryStatus): boolean {
        return this.SUCCESSFUL_STATUSES.has(status);
    }

    /**
     * Check if status indicates device needs remediation
     */
    static isActionable(status: DiscoveryStatus): boolean {
        return this.ACTIONABLE_STATUSES.has(status);
    }

    /**
     * Check if status indicates device is unreachable
     */
    static isUnreachable(status: DiscoveryStatus): boolean {
        return this.UNREACHABLE_STATUSES.has(status);
    }
}

/**
 * Discovery result structure
 */
interface DiscoveryResult {
    ip: string;
    status: DiscoveryStatus;
    inventory?: DeviceInventory | null;
    error?: string;
}

/**
 * Canister discovery result wrapper
 */
interface CanisterDiscoveryResult {
    success: boolean;
    data?: any;
    error?: string;
}

/**
 * Config metadata tuple for vector_db
 */
type ConfigMetadataTuple = [string, string];

/**
 * Window interface extensions for config manager
 */
interface ConfigManager {
    getAll(): {
        discoveryHub?: {
            url?: string;
            port?: number;
            endpoints?: {
                dcmsDiscovery?: string;
            };
        };
        proxy?: {
            enabled?: boolean;
            port?: number;
        };
    };
}

declare global {
    interface Window {
        configManager?: ConfigManager;
        appCore?: any;
        DCMSDiscovery: typeof DCMSDiscovery;
        runDCMSDiscovery: (targetInput?: string | null) => Promise<any>;
    }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Expand CIDR notation to list of IP addresses
 * Skips network (.0) and broadcast (.255) addresses for /24 networks
 * Limits to 254 usable IPs for performance
 *
 * @param cidr - CIDR notation string (e.g., "192.168.1.0/24")
 * @returns Array of usable IP addresses
 */
export function expandCIDR(cidr: string): string[] {
    const [baseIP, maskSize] = cidr.split('/');
    const mask = parseInt(maskSize);

    // Convert IP to 32-bit integer
    const ipToInt = (ip: string): number => {
        const parts = ip.split('.').map(Number);
        return (parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3];
    };

    // Convert integer back to IP
    const intToIP = (int: number): string => {
        return [
            (int >>> 24) & 255,
            (int >>> 16) & 255,
            (int >>> 8) & 255,
            int & 255
        ].join('.');
    };

    const baseInt = ipToInt(baseIP);
    const numHosts = Math.pow(2, 32 - mask);

    // For standard /24 networks, skip network address (.0) and broadcast (.255)
    // For larger networks, skip first and last address
    const startOffset = (numHosts > 2) ? 1 : 0;
    const endOffset = (numHosts > 2) ? 1 : 0;
    const usableHosts = numHosts - startOffset - endOffset;

    // For large ranges, limit to first 254 usable IPs for performance
    const maxIPs = Math.min(usableHosts, 254);
    const ips: string[] = [];

    for (let i = 0; i < maxIPs; i++) {
        ips.push(intToIP(baseInt + startOffset + i));
    }

    return ips;
}

// ============================================================================
// DCMSDiscovery Class
// ============================================================================

/**
 * Main DCMS Discovery class
 * Handles network device discovery via Discovery Hub with credential-based authentication
 */
export class DCMSDiscovery {
    private logger: DiscoveryLogger | null;
    private canisterClient: CanisterClient | null;
    private statusDiv: HTMLElement | null;
    private resultsDiv: HTMLElement | null;

    constructor() {
        this.logger = null;
        this.canisterClient = null;
        this.statusDiv = null;
        this.resultsDiv = null;
        this.initializeElements();
    }

    /**
     * Initialize DOM elements for status and results display
     */
    private initializeElements(): void {
        this.statusDiv = document.getElementById('discovery-status');
        this.resultsDiv = document.getElementById('discovery-results');
    }

    /**
     * Set external dependencies (logger and canister client)
     * Called by app-core during initialization
     */
    setDependencies(logger: DiscoveryLogger, canisterClient: CanisterClient): void {
        console.log('🔧 DCMSDiscovery.setDependencies called with:', {
            logger: !!logger,
            canisterClient: !!canisterClient
        });
        this.logger = logger;
        this.canisterClient = canisterClient;
        console.log('✅ DCMSDiscovery dependencies set successfully');
    }

    /**
     * Main DCMS discovery function
     * Orchestrates the entire discovery process from input to results
     */
    async runDiscovery(targetInput: string | null = null): Promise<any> {
        try {
            // Get target input from UI if not provided
            if (!targetInput) {
                const targetInputElement = document.getElementById('target-range-input') as HTMLInputElement;
                if (targetInputElement) {
                    targetInput = targetInputElement.value.trim();
                }
            }

            if (!targetInput) {
                throw new Error('Please enter a target IP address in the "Target Range / IPs" field');
            }

            // Initialize discovery session
            this.initializeDiscovery(targetInput);

            // Validate input
            if (!this.isValidIPInput(targetInput)) {
                throw new Error('Please enter a valid IP address, CIDR range (e.g., 192.168.1.0/24), IP range (e.g., 192.168.1.1-192.168.1.50), or comma-separated IPs.');
            }

            // Parse and expand target input
            const { targetIPs, discoveryType } = this.parseTargetInput(targetInput);

            // Get discovery configuration
            const config = this.getDiscoveryConfig();

            // Log discovery start
            this.logDiscoveryStart(targetInput, targetIPs, discoveryType, config);

            // Update status UI
            this.updateStatus('connecting', targetInput, targetIPs.length, discoveryType, config);

            // Perform discovery
            const canisterResult = await this.performDiscovery(targetIPs, discoveryType);

            if (!canisterResult.success) {
                throw new Error(canisterResult.error || 'Discovery failed');
            }

            // Process and display results
            const results = this.processResults(canisterResult.data, targetIPs);
            await this.logResults(results, targetIPs, discoveryType);
            this.updateStatus('success', targetInput, results.length, discoveryType, config);
            this.displayResults(results, targetIPs);

            console.log('✅ DCMS Discovery completed successfully:', canisterResult.data);

            // Trigger dashboard refresh if PageManager is available
            if ((window as any).pageManagement) {
                console.log('🔄 Refreshing dashboard stats after discovery...');
                // Use setTimeout to allow discovery UI to finish updating first
                setTimeout(() => {
                    const currentPage = document.querySelector('[id$="-page"]:not(.hidden)');
                    if (currentPage && currentPage.id === 'dashboard-page') {
                        // If on dashboard, refresh immediately
                        (window as any).showPage('dashboard');
                    }
                }, 500);
            }

            return canisterResult.data;

        } catch (error) {
            console.error('❌ DCMS Discovery failed:', error);
            this.handleError(error as Error);
            throw error;
        }
    }

    /**
     * Initialize discovery session and get UI options
     */
    private initializeDiscovery(_targetInput: string): {
        discoveryMethod: string;
        credentials: string;
        detailedLogging: boolean;
        generateSpreadsheet: boolean;
    } {
        // Show discovery log container and clear previous logs
        if (this.logger) {
            this.logger.startSession('DCMS Discovery');
        }

        // Get discovery options from the UI
        const discoveryMethod = (document.querySelector('input[name="discovery-method"]:checked') as HTMLInputElement)?.value || 'dcms-credential';
        const credentials = (document.querySelector('select[name="credentials"]') as HTMLSelectElement)?.value || 'ada-robot';
        const detailedLogging = (document.querySelector('input[name="detailed-logging"]') as HTMLInputElement)?.checked || false;
        const generateSpreadsheet = (document.querySelector('input[name="generate-spreadsheet"]') as HTMLInputElement)?.checked || false;

        return { discoveryMethod, credentials, detailedLogging, generateSpreadsheet };
    }

    /**
     * Get Discovery Hub configuration from config manager
     */
    private getDiscoveryConfig(): DiscoveryConfig {
        // Get Discovery Hub configuration from config manager
        const config = window.configManager ? window.configManager.getAll() : {};
        const discoveryHub = {
            ip: config.discoveryHub?.url?.replace('http://', '') || 'localhost',
            port: config.proxy?.enabled ? config.proxy.port || 8082 : config.discoveryHub?.port || 8082,
            endpoint: config.discoveryHub?.endpoints?.dcmsDiscovery || '/discover/ip/'
        };

        const discoveryMethod = (document.querySelector('input[name="discovery-method"]:checked') as HTMLInputElement)?.value || 'dcms-credential';
        const credentials = (document.querySelector('select[name="credentials"]') as HTMLSelectElement)?.value || 'ada-robot';
        const detailedLogging = (document.querySelector('input[name="detailed-logging"]') as HTMLInputElement)?.checked || false;
        const generateSpreadsheet = (document.querySelector('input[name="generate-spreadsheet"]') as HTMLInputElement)?.checked || false;

        return { discoveryHub, discoveryMethod, credentials, detailedLogging, generateSpreadsheet };
    }

    /**
     * Validate IP input format
     * Supports single IP, CIDR, IP range, and comma-separated IPs
     */
    private isValidIPInput(input: string): boolean {
        // Check for single IP address
        const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
        // Check for CIDR notation (e.g., 192.168.1.0/24)
        const cidrPattern = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
        // Check for IP range (e.g., 192.168.1.1-192.168.1.50)
        const rangePattern = /^(\d{1,3}\.){3}\d{1,3}-(\d{1,3}\.){3}\d{1,3}$/;

        // If it's a CIDR notation
        if (cidrPattern.test(input)) {
            const [ip, cidr] = input.split('/');
            const cidrNum = parseInt(cidr);
            if (cidrNum < 0 || cidrNum > 32) return false;
            const parts = ip.split('.').map(Number);
            return parts.every(part => part >= 0 && part <= 255);
        }

        // If it's a range
        if (rangePattern.test(input)) {
            const [startIP, endIP] = input.split('-');
            return this.isValidSingleIP(startIP) && this.isValidSingleIP(endIP);
        }

        // If it's a single IP
        if (ipPattern.test(input)) {
            return this.isValidSingleIP(input);
        }

        // Check for comma-separated IPs
        if (input.includes(',')) {
            const ips = input.split(',').map(ip => ip.trim());
            return ips.every(ip => this.isValidSingleIP(ip));
        }

        return false;
    }

    /**
     * Validate a single IP address
     */
    private isValidSingleIP(ip: string): boolean {
        const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!ipPattern.test(ip)) return false;
        const parts = ip.split('.').map(Number);
        return parts.every(part => part >= 0 && part <= 255);
    }

    /**
     * Parse target input and expand to list of IPs
     * Handles CIDR, ranges, and comma-separated lists
     */
    private parseTargetInput(targetInput: string): ParsedTargetInput {
        let targetIPs: string[] = [];
        let discoveryType: 'single' | 'cidr' | 'range' | 'multiple' = 'single';

        if (targetInput.includes('/')) {
            // CIDR notation
            if (this.logger) {
                this.logger.logInfo(`🔍 Parsing CIDR notation: ${targetInput}`);
            }
            targetIPs = this.expandCIDR(targetInput);
            discoveryType = 'cidr';
            if (this.logger) {
                this.logger.logSuccess(`📊 Expanded CIDR ${targetInput} to ${targetIPs.length} IPs`);
            }
        } else if (targetInput.includes('-')) {
            // IP range
            if (this.logger) {
                this.logger.logInfo(`🔍 Parsing IP range: ${targetInput}`);
            }
            const [startIP, endIP] = targetInput.split('-').map(ip => ip.trim());
            targetIPs = [startIP, endIP]; // For now, just use the two endpoints
            discoveryType = 'range';
            if (this.logger) {
                this.logger.logSuccess(`📊 IP range parsed: ${targetIPs.length} endpoints`);
            }
        } else if (targetInput.includes(',')) {
            // Comma-separated IPs
            if (this.logger) {
                this.logger.logInfo(`🔍 Parsing comma-separated IPs: ${targetInput}`);
            }
            targetIPs = targetInput.split(',').map(ip => ip.trim());
            discoveryType = 'multiple';
            if (this.logger) {
                this.logger.logSuccess(`📊 Found ${targetIPs.length} individual IPs`);
            }
        } else {
            // Single IP
            targetIPs = [targetInput];
            discoveryType = 'single';
            if (this.logger) {
                this.logger.logInfo(`🔍 Single IP target: ${targetInput}`);
            }
        }

        return { targetIPs, discoveryType };
    }

    /**
     * Expand CIDR notation to list of IP addresses
     * Skips network (.0) and broadcast (.255) addresses for /24 networks
     * Limits to 254 usable IPs for performance
     *
     * Note: Delegates to standalone expandCIDR() function for maintainability
     */
    private expandCIDR(cidr: string): string[] {
        return expandCIDR(cidr);
    }

    /**
     * Log discovery start information
     */
    private logDiscoveryStart(
        targetInput: string,
        targetIPs: string[],
        discoveryType: string,
        config: DiscoveryConfig
    ): void {
        if (!this.logger) return;

        this.logger.logPhase('🚀 DCMS Discovery Started');
        this.logger.logInfo(`📋 Target: ${targetInput}`);
        this.logger.logInfo(`🌐 Discovery Hub: ${config.discoveryHub.ip}:${config.discoveryHub.port}`);
        this.logger.logInfo(`🔐 Method: ${config.discoveryMethod} | Credentials: ${config.credentials}`);
        this.logger.logSpacer();

        if (discoveryType === 'single') {
            this.logger.logPhase(`🎯 Starting discovery for device: ${targetIPs[0]}`);
        } else {
            this.logger.logPhase(`🎯 Starting batch discovery for ${targetIPs.length} devices`);
            this.logger.logInfo(`📋 Discovery type: ${discoveryType}`);
        }

        this.logger.logInfo(`🌐 Connecting to Discovery Hub: ${config.discoveryHub.ip}:${config.discoveryHub.port}`);
    }

    /**
     * Perform discovery via canister client
     * Handles both single IP and batch discovery
     */
    private async performDiscovery(
        targetIPs: string[],
        _discoveryType: string
    ): Promise<CanisterDiscoveryResult> {
        if (!this.canisterClient) {
            throw new Error('Canister client not available');
        }

        if (targetIPs.length === 1) {
            // Single IP - use direct discovery
            const targetIp = targetIPs[0];
            if (this.logger) {
                this.logger.logInfo(`🔍 Probing device: ${targetIp}`);
            }
            return await this.canisterClient.discoverViaDirectHub(targetIp);
        } else {
            // Multiple IPs - use range discovery with logging callback
            if (this.logger) {
                this.logger.logInfo(`🔍 Starting batch discovery for ${targetIPs.length} devices`);
                this.logger.logDetail(`📊 Processing in batches of 10 devices...`);
            }
            const logCallback = this.logger ? (msg: string, type: string) => this.logger!.log(msg, type as any) : null;
            return await this.canisterClient.discoverRangeViaDirectHub(targetIPs, logCallback);
        }
    }

    /**
     * Process discovery results
     * Handles both single and array result formats
     */
    private processResults(data: any, _targetIPs: string[]): DiscoveryResult[] {
        // Handle different result formats (single vs multiple)
        const results: DiscoveryResult[] = Array.isArray(data) ? data : [data];
        return results;
    }

    /**
     * Process and store discovered configuration files in vector_db
     *
     * This method is critical for the VC demo - it stores real config files
     * discovered from AWS EC2 instances into the vector database with embeddings.
     *
     * @param configs - Array of discovered configuration files
     * @param deviceIp - IP address of the device
     * @public Made public to allow access from runDCMSDiscovery standalone function
     */
    public async processAndStoreConfigs(configs: DiscoveredConfig[], deviceIp: string): Promise<void> {
        if (!configs || configs.length === 0) return;

        let successCount = 0;
        let failCount = 0;

        for (const config of configs) {
            try {
                // Generate a unique config ID
                const configId = `${deviceIp}_${config.path.replace(/\//g, '_')}`;

                // Extract config details
                const configPath = config.path || 'unknown';
                const configType = config.type || 'unknown';
                const configContent = config.content || '';

                // Generate a simple embedding (128 dimensions)
                // In a production system, this would use a real embedding model
                const embedding = this.generateSimpleEmbedding(configContent);

                // Prepare metadata as array of tuples for Candid
                // NOTE: service_name removed - services are discovered dynamically via connection analysis
                const metadata: ConfigMetadataTuple[] = [
                    ['device_id', config.device_id || deviceIp],
                    ['discovered_at', config.discovered_at || new Date().toISOString()],
                    ['size_bytes', String(config.size_bytes || configContent.length)]
                ];

                if (this.logger) {
                    this.logger.logDetail(`   📄 Storing: ${configPath} (${configType})`);
                }

                // Store in vector_db canister using wrapper method
                const result = await this.canisterClient!.storeConfig(
                    configId,
                    deviceIp,
                    configPath,
                    configContent,
                    configType,
                    embedding as any,
                    metadata as any
                );

                // Check for proxy response format (result.success) or Candid variant format (result.Ok)
                if ((result && (result as any).success) || (result && (result as any).Ok !== undefined)) {
                    successCount++;
                    if (this.logger) {
                        this.logger.logSuccess(`   ✅ Stored: ${configPath}`);
                    }
                } else {
                    failCount++;
                    const errorMsg = ((result as any) && (result as any).error) || ((result as any) && (result as any).Err) || 'Unknown error';
                    if (this.logger) {
                        this.logger.logWarning(`   ⚠️ Failed to store ${configPath}: ${errorMsg}`);
                    }
                }
            } catch (error) {
                failCount++;
                if (this.logger) {
                    this.logger.logWarning(`   ⚠️ Error storing ${config.path}: ${(error as Error).message}`);
                }
            }
        }

        if (this.logger) {
            this.logger.logSuccess(`📊 Config storage complete: ${successCount} stored, ${failCount} failed`);
            this.logger.logSpacer();
        }
    }

    /**
     * Store device using centralized Storage Manager (OOA/OOD Design)
     *
     * This is the NEW unified method that calls the Storage Manager.
     * Replaces processAndStoreDevice() + processAndStoreConfigs() with ONE call.
     *
     * @param device Discovery result containing device and config data
     */
    public async storeDeviceViaStorageManager(device: DiscoveryResult): Promise<void> {
        if (!this.canisterClient) {
            console.error('Canister client not initialized');
            return;
        }

        // Skip devices with null inventory (network devices, gateways, unreachable hosts)
        if (!device.inventory || device.inventory === null) {
            if (this.logger) {
                this.logger.logInfo(`ℹ️ Skipping ${device.ip}: No inventory data (likely network device or inaccessible)`);
            }
            return;
        }

        try {
            // Build StorageRequest from DiscoveryResult
            const storageRequest = this.buildStorageRequest(device);

            // Single call to Storage Manager
            const result = await this.canisterClient.storeDiscoveredDevice(storageRequest);

            // Handle response
            this.handleStorageResponse(result, device.ip);

        } catch (error) {
            console.error('Error storing device via Storage Manager:', error);
            if (this.logger) {
                this.logger.logWarning(`❌ Error storing device: ${(error as Error).message}`);
            }
        }
    }

    /**
     * Transform DiscoveryResult to StorageRequest
     * Simple field mapping - no business logic
     */
    private buildStorageRequest(device: DiscoveryResult): any {
        // Use hostname if available, otherwise use IP address
        const deviceId = device.inventory?.hostname || device.ip;

        // 1. Device metadata
        const deviceMetadata: Array<[string, string]> = [];
        if (device.inventory?.hostname) {
            deviceMetadata.push(['hostname', device.inventory.hostname]);
        }
        if (device.inventory?.collection_method) {
            deviceMetadata.push(['method', device.inventory.collection_method]);
        }

        // Extract OS information from os_info field
        // NOTE: This logic mirrors the Rust backend in aws_discovery_integration/src/lib.rs:785-833
        // If you modify this, please update both implementations to keep them in sync
        let osName: string | null = null;
        let osVersion: string | null = null;

        if (device.inventory?.os_info) {
            const osInfo = device.inventory.os_info;

            // Extract PRETTY_NAME (e.g., "Ubuntu 20.04.6 LTS")
            const prettyNameMatch = osInfo.match(/PRETTY_NAME="([^"]+)"/);
            if (prettyNameMatch) {
                osName = prettyNameMatch[1];
            }

            // Extract VERSION_ID (e.g., "20.04")
            const versionIdMatch = osInfo.match(/VERSION_ID="([^"]+)"/);
            if (versionIdMatch) {
                osVersion = versionIdMatch[1];
            }

            // Fallback: if extraction failed, use first line
            if (!osName && osInfo.trim().length > 0) {
                const firstLine = osInfo.split('\n')[0];
                if (firstLine) {
                    osName = firstLine.trim();
                }
            }
        }

        // Add OS information to metadata
        if (osName) {
            deviceMetadata.push(['os', osName]);
        }
        if (osVersion) {
            deviceMetadata.push(['os_version', osVersion]);
        }

        // Classify device type using hostname first, then IP, then OS fallback
        // NOTE: This logic is mirrored in Rust at:
        // src/aws_discovery_integration/src/storage_integration.rs
        // If you modify this, please update both implementations to keep them in sync
        let deviceType = 'Server';
        const hostnameLower = (device.inventory?.hostname || '').toLowerCase();
        const osLower = (device.inventory?.os_info || '').toLowerCase();
        const ip = device.ip || '';

        // IP-based classification (known infrastructure mapping)
        const ipTypeMap: Record<string, string> = {
            '192.168.1.20': 'Database',      // db-oracle-01
            '192.168.1.35': 'Backup',        // backup-veeam-01
            '192.168.1.50': 'Network',       // lb-f5-01
            '192.168.1.30': 'Virtualization', // vm-esxi-01
            '192.168.1.100': 'Storage',      // san-storage-01
            '192.168.1.25': 'Web',           // web-nginx-01
        };

        if (hostnameLower.startsWith('db-') || hostnameLower.includes('oracle') || hostnameLower.includes('mysql') || hostnameLower.includes('postgres')) {
            deviceType = 'Database';
        } else if (hostnameLower.startsWith('web-') || hostnameLower.includes('nginx') || hostnameLower.includes('apache')) {
            deviceType = 'Web';
        } else if (hostnameLower.startsWith('vm-') || hostnameLower.includes('esxi') || hostnameLower.includes('vcenter') || hostnameLower.includes('hyperv')) {
            deviceType = 'Virtualization';
        } else if (hostnameLower.startsWith('backup-') || hostnameLower.includes('veeam') || hostnameLower.includes('commvault')) {
            deviceType = 'Backup';
        } else if (hostnameLower.startsWith('lb-') || hostnameLower.includes('fw-') || hostnameLower.includes('f5') || hostnameLower.includes('haproxy')) {
            deviceType = 'Network';
        } else if (hostnameLower.startsWith('san-') || hostnameLower.includes('netapp') || hostnameLower.includes('purestorage')) {
            deviceType = 'Storage';
        } else if (hostnameLower.startsWith('srv-') || hostnameLower.startsWith('app-')) {
            deviceType = 'Server';
        } else if (ip in ipTypeMap) {
            deviceType = ipTypeMap[ip];
        } else if (osLower.includes('f5') || osLower.includes('tmos')) {
            deviceType = 'Network';
        } else if (osLower.includes('netapp') || osLower.includes('storage')) {
            deviceType = 'Storage';
        } else if (osLower.includes('esxi') || osLower.includes('vmware')) {
            deviceType = 'Virtualization';
        }

        // 2. Config files (direct mapping)
        const configFiles = (device.inventory?.discovered_configs || []).map(config => ({
            path: config.path || 'unknown',
            content: config.content || '',
            config_type: config.type || 'unknown',
            metadata: [
                ['device_id', config.device_id || deviceId],
                ['discovered_at', config.discovered_at || new Date().toISOString()],
                ['size_bytes', String(config.size_bytes || 0)]
            ]
        }));

        // 3. Inventory data (if available) - use [] for Candid opt None
        let inventoryData: any[] = []; // Candid opt None = empty array
        if (device.inventory) {
            const fields: Array<[string, string]> = [];
            if (device.inventory.hostname) fields.push(['hostname', device.inventory.hostname]);
            if (device.inventory.os_type) fields.push(['os_type', device.inventory.os_type]);
            if (device.inventory.collection_method) fields.push(['collection_method', device.inventory.collection_method]);

            if (fields.length > 0) {
                inventoryData = [{
                    hardware_id: [], // Candid opt None = empty array
                    fields: fields,
                    agent: device.inventory.collection_method ? [device.inventory.collection_method] : [],
                    inventory_date: [new Date().toISOString()]
                }];
            }
        }

        // 4. Return complete request
        return {
            device_data: {
                device_id: deviceId,
                ip: device.ip,
                hostname: device.inventory?.hostname ? [device.inventory.hostname] : [],
                os: osName ? [osName] : [], // Extracted from os_info - wrapped in array for Candid opt
                os_version: osVersion ? [osVersion] : [], // Extracted from os_info - wrapped in array for Candid opt
                device_type: deviceType,
                status: 'Online', // Successfully discovered devices are Online
                metadata: deviceMetadata
            },
            config_files: configFiles,
            inventory_data: inventoryData,
            applications: [], // Can be enhanced later
            discovery_metadata: {
                discovery_timestamp: BigInt(Date.now() * 1_000_000), // ms to ns
                discovery_method: device.inventory?.collection_method || 'hub',
                session_id: [], // Candid opt None = empty array
                source: 'dcms-discovery-frontend'
            }
        };
    }

    /**
     * Handle Storage Manager response
     * Logs summary and warnings to UI
     */
    private handleStorageResponse(result: any, deviceId: string): void {
        if ('Ok' in result) {
            const response = result.Ok;
            const summary = response.summary;

            if (this.logger) {
                this.logger.logSuccess(`✅ Device ${deviceId} stored successfully`);
                this.logger.logDetail(`   🆔 sys_id: ${summary.sys_id}`);
                this.logger.logDetail(`   📦 Configs: ${summary.configs_stored}`);
                this.logger.logDetail(`   📊 Inventory: ${summary.inventory_stored ? 'Yes' : 'No'}`);
                this.logger.logDetail(`   ⚡ Time: ${summary.storage_time_ms}ms`);

                // Log warnings if any
                if (response.warnings && response.warnings.length > 0) {
                    for (const warning of response.warnings) {
                        this.logger.logWarning(`   ⚠️  ${warning}`);
                    }
                }
            }
        } else if ('Err' in result) {
            const error = result.Err;
            console.error(`Storage failed for ${deviceId}:`, error);
            if (this.logger) {
                this.logger.logWarning(`❌ Storage failed for ${deviceId}`);
            }
        }
    }


    /**
     * Generate a simple 128-dimensional embedding based on text content
     *
     * CRITICAL: This method must return number[] (array of float values) for type safety.
     * The embedding values are in the range [0.01, 1.00] to ensure proper float32 encoding.
     *
     * This is a placeholder implementation - in production, use a real embedding model
     * like OpenAI's text-embedding-ada-002 or similar.
     *
     * @param text - The text content to generate an embedding for
     * @returns Array of 128 float values representing the embedding
     */
    private generateSimpleEmbedding(text: string): number[] {
        // Generate a simple 128-dimensional embedding based on text content
        // This is a placeholder - in production, use a real embedding model
        const embedding = new Float32Array(128);

        // Simple hash-based embedding generation
        // Add 0.01 offset to ensure all values are proper float32 (range 0.01-1.00)
        // This prevents integer 0 values which cause Candid serialization errors
        for (let i = 0; i < 128; i++) {
            let hash = 0;
            for (let j = 0; j < text.length; j++) {
                hash = ((hash << 5) - hash + text.charCodeAt(j) + i) | 0;
            }
            embedding[i] = ((hash % 100) + 0.01) / 100.0;
        }

        return Array.from(embedding);
    }

    /**
     * Log discovery results and process config files
     */
    private async logResults(
        results: DiscoveryResult[],
        targetIPs: string[],
        discoveryType: string
    ): Promise<void> {
        if (!this.logger) return;

        // Count devices that were successfully discovered using status classifier
        const discoveredCount = results.filter(r =>
            DiscoveryStatusClassifier.isSuccessful(r.status)
        ).length;

        this.logger.logSpacer();
        if (results.length === 1) {
            const device = results[0];
            // Process devices with successful discovery status
            if (DiscoveryStatusClassifier.isSuccessful(device.status)) {
                this.logger.logSuccess(`✅ Device ${targetIPs[0]} discovered successfully`);
                if (device.inventory?.hostname) {
                    this.logger.logDetail(`   📛 Hostname: ${device.inventory.hostname}`);
                }
                if (device.inventory?.accessible !== undefined) {
                    this.logger.logDetail(`   🔗 Accessible: ${device.inventory.accessible ? 'Yes' : 'No'}`);
                }
                if (device.inventory?.method) {
                    this.logger.logDetail(`   🔧 Method: ${device.inventory.method}`);
                }

                // Store device record using centralized Storage Manager
                // This ONE call stores: device, configs, inventory, apps, relationships
                await this.storeDeviceViaStorageManager(device);
            } else {
                this.logger.logWarning(`❌ Device ${targetIPs[0]} is offline or unreachable`);
            }
        } else {
            this.logger.logSuccess(`🎉 Batch discovery completed: ${discoveredCount}/${results.length} devices discovered`);
            this.logger.logInfo(`📊 Discovery type: ${discoveryType}`);

            // Store device records for batch discovery using Storage Manager
            for (const device of results) {
                // Process devices with successful discovery status
                if (DiscoveryStatusClassifier.isSuccessful(device.status)) {
                    // Store using centralized Storage Manager (ONE call per device)
                    await this.storeDeviceViaStorageManager(device);
                }
            }
        }

        if (this.logger) {
            this.logger.endSession('DCMS Discovery', true);
        }
    }

    /**
     * Update status UI based on discovery state
     */
    private updateStatus(
        state: 'connecting' | 'success' | 'error',
        targetInput: string,
        count: number,
        discoveryType: string,
        config: DiscoveryConfig
    ): void {
        if (!this.statusDiv) return;

        switch (state) {
            case 'connecting':
                this.statusDiv.innerHTML = `
                    <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div class="flex items-center">
                            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-3"></div>
                            <span class="text-yellow-800 font-medium">Connecting to Discovery Hub...</span>
                        </div>
                        <div class="mt-2 text-sm text-yellow-600">
                            Discovering ${discoveryType === 'single' ? 'device' : `${count} devices`}: ${targetInput}<br>
                            Via hub: ${config.discoveryHub.ip}:${config.discoveryHub.port}
                        </div>
                    </div>`;
                break;

            case 'success':
                this.statusDiv.innerHTML = `
                    <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <div class="flex items-center">
                            <div class="h-4 w-4 text-green-600 mr-3 flex items-center justify-center font-bold">✓</div>
                            <span class="text-green-800 font-medium">DCMS Discovery Completed Successfully</span>
                        </div>
                        <div class="mt-2 text-sm text-green-600">
                            Discovery completed for ${discoveryType === 'single' ? 'device' : `${count} devices`}
                        </div>
                    </div>`;
                break;

            case 'error':
                this.statusDiv.innerHTML = `
                    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <div class="flex items-center">
                            <div class="h-4 w-4 text-red-600 mr-3 flex items-center justify-center font-bold">!</div>
                            <span class="text-red-800 font-medium">DCMS Discovery Failed</span>
                        </div>
                    </div>`;
                break;
        }

        // Feather icons replaced with CSS-based icons to avoid library conflicts
    }

    /**
     * Display discovery results in UI
     */
    private displayResults(results: DiscoveryResult[], targetIPs: string[]): void {
        if (!this.resultsDiv) return;

        if (results.length === 1 && results[0].status === 'discovered') {
            this.displaySingleDeviceResults(targetIPs[0], results[0]);
        } else if (results.length > 1) {
            this.displayRangeResults(results, targetIPs);
        }
    }

    /**
     * Display single device discovery results
     */
    private displaySingleDeviceResults(ip: string, discoveryResult: DiscoveryResult): void {
        if (!this.resultsDiv) return;

        const inventory = discoveryResult.inventory || {} as DeviceInventory;

        this.resultsDiv.innerHTML = `
            <div class="bg-white border border-gray-200 rounded-lg p-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">DCMS Discovery Results - ${ip}</h3>
                <div class="space-y-4">
                    <div class="border-l-4 border-green-500 pl-4">
                        <p class="text-sm text-gray-600">Status</p>
                        <p class="font-medium text-green-600">${discoveryResult.status}</p>
                    </div>
                    ${inventory.hostname ? `
                    <div class="border-l-4 border-blue-500 pl-4">
                        <p class="text-sm text-gray-600">Hostname</p>
                        <p class="font-medium">${inventory.hostname}</p>
                    </div>` : ''}
                    ${inventory.accessible !== undefined ? `
                    <div class="border-l-4 border-${inventory.accessible ? 'green' : 'red'}-500 pl-4">
                        <p class="text-sm text-gray-600">SSH Access</p>
                        <p class="font-medium text-${inventory.accessible ? 'green' : 'red'}-600">
                            ${inventory.accessible ? 'Accessible' : 'Not Accessible'}
                        </p>
                    </div>` : ''}
                    ${inventory.method ? `
                    <div class="border-l-4 border-purple-500 pl-4">
                        <p class="text-sm text-gray-600">Discovery Method</p>
                        <p class="font-medium">${inventory.method}</p>
                    </div>` : ''}
                </div>
            </div>
        `;
    }

    /**
     * Display batch discovery results
     */
    private displayRangeResults(results: DiscoveryResult[], targetIPs: string[]): void {
        if (!this.resultsDiv) return;

        const discoveredCount = results.filter(r => r.status === 'discovered').length;

        let html = `
            <div class="bg-white border border-gray-200 rounded-lg p-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">
                    DCMS Batch Discovery Results - ${discoveredCount}/${results.length} devices discovered
                </h3>
                <div class="space-y-3">
        `;

        results.forEach((result, index) => {
            const ip = result.ip || targetIPs[index] || `Device ${index + 1}`;
            const isDiscovered = result.status === 'discovered';
            const statusColor = isDiscovered ? 'green' : 'red';

            html += `
                <div class="border-l-4 border-${statusColor}-500 pl-4 py-2">
                    <div class="flex justify-between items-center">
                        <p class="font-medium">${ip}</p>
                        <span class="text-sm text-${statusColor}-600 font-medium">${result.status}</span>
                    </div>
                    ${result.inventory?.hostname ? `
                    <p class="text-sm text-gray-600">Hostname: ${result.inventory.hostname}</p>
                    ` : ''}
                    ${result.error ? `
                    <p class="text-sm text-red-600">Error: ${result.error}</p>
                    ` : ''}
                </div>
            `;
        });

        html += `
                </div>
            </div>
        `;

        this.resultsDiv.innerHTML = html;
    }

    /**
     * Handle discovery errors
     */
    private handleError(error: Error): void {
        if (this.logger) {
            this.logger.logSpacer();
            this.logger.logError(`❌ DCMS Discovery Failed: ${error.message}`);
            this.logger.endSession('DCMS Discovery', false);
        }

        this.updateStatus('error', '', 0, '', {} as DiscoveryConfig);
    }
}

// ============================================================================
// Browser Integration
// ============================================================================

// Create global class
window.DCMSDiscovery = DCMSDiscovery;

// Helper function to display pre-check status in the UI
function displayPreCheckStatus(preCheckResult: any): void {
    const container = document.getElementById('precheck-status-container');
    const detailsDiv = document.getElementById('precheck-status-details');

    if (!container || !detailsDiv) {
        console.warn('Pre-check status elements not found in DOM');
        return;
    }

    // Hide the "Preparing..." toast as soon as status appears
    if ((window as any).preparingDiscoveryToastId) {
        (window as any).toastNotifications.hide((window as any).preparingDiscoveryToastId);
        (window as any).preparingDiscoveryToastId = null;
    }

    // Show the container
    container.classList.remove('hidden');

    // Determine overall status and style
    const isSuccess = preCheckResult.success === true;
    const timestamp = preCheckResult.data?.timestamp || new Date().toISOString();

    // Update container styling based on success/failure
    if (isSuccess) {
        container.className = 'mt-4 p-3 bg-green-50 rounded border border-green-300';
        container.querySelector('p')!.className = 'text-sm font-semibold mb-2 text-green-900';
        container.querySelector('p')!.textContent = '✓ System Pre-Check Status: PASSED';
    } else {
        container.className = 'mt-4 p-3 bg-red-50 rounded border border-red-300';
        container.querySelector('p')!.className = 'text-sm font-semibold mb-2 text-red-900';
        container.querySelector('p')!.textContent = '✗ System Pre-Check Status: FAILED';
    }

    // Build detailed status HTML
    let detailsHtml = `<p class="text-xs text-gray-600 mb-2">Last checked: ${new Date(timestamp).toLocaleString()}</p>`;

    if (preCheckResult.data?.checks) {
        const checks = preCheckResult.data.checks;

        // Proxy status
        const proxyStatus = checks.proxy?.status || 'unknown';
        const isProxyNA = proxyStatus.includes('N/A');
        const proxyIcon = proxyStatus === 'running' ? '✓' : (isProxyNA ? '○' : '✗');
        const proxyColor = proxyStatus === 'running' ? 'text-green-700' : (isProxyNA ? 'text-gray-500' : 'text-red-700');
        detailsHtml += `
            <div class="flex items-center gap-2 mb-1">
                <span class="${proxyColor} font-bold">${proxyIcon}</span>
                <span class="text-xs font-medium">Canister Proxy:</span>
                <span class="text-xs ${proxyColor}">${proxyStatus}</span>
                <span class="text-xs text-gray-500">(port ${checks.proxy?.port || 5001})</span>
            </div>
        `;

        // EC2 status
        const ec2Status = checks.ec2?.status || 'unknown';
        const ec2Icon = (ec2Status === 'running' || ec2Status === 'starting') ? '✓' : '✗';
        const ec2Color = (ec2Status === 'running' || ec2Status === 'starting') ? 'text-green-700' : 'text-red-700';
        detailsHtml += `
            <div class="flex items-center gap-2 mb-1">
                <span class="${ec2Color} font-bold">${ec2Icon}</span>
                <span class="text-xs font-medium">EC2 Instances:</span>
                <span class="text-xs ${ec2Color}">${ec2Status}</span>
                <span class="text-xs text-gray-500">(${checks.ec2?.region || 'ap-southeast-2'})</span>
            </div>
        `;

        // Discovery Hub status
        const hubStatus = checks.discovery_hub?.status || 'unknown';
        const hubIcon = hubStatus === 'reachable' ? '✓' : '✗';
        const hubColor = hubStatus === 'reachable' ? 'text-green-700' : 'text-red-700';
        detailsHtml += `
            <div class="flex items-center gap-2 mb-1">
                <span class="${hubColor} font-bold">${hubIcon}</span>
                <span class="text-xs font-medium">Discovery Hub:</span>
                <span class="text-xs ${hubColor}">${hubStatus}</span>
                <span class="text-xs text-gray-500">(${checks.discovery_hub?.url || 'http://unboundata.com:8080'})</span>
            </div>
        `;
    }

    // Show errors if any
    if (preCheckResult.data?.errors && preCheckResult.data.errors.length > 0) {
        const errors = preCheckResult.data.errors.filter((e: string) => e && e.trim());
        if (errors.length > 0) {
            detailsHtml += `
                <div class="mt-3 pt-2 border-t border-red-200">
                    <p class="text-xs font-semibold text-red-900 mb-1">Errors:</p>
                    ${errors.map((err: string) => `
                        <p class="text-xs text-red-700 ml-4">• ${err}</p>
                    `).join('')}
                </div>
            `;
        }
    }

    // Show warnings if any
    if (preCheckResult.data?.warnings && preCheckResult.data.warnings.length > 0) {
        const warnings = preCheckResult.data.warnings.filter((w: string) => w && w.trim());
        if (warnings.length > 0) {
            detailsHtml += `
                <div class="mt-3 pt-2 border-t border-yellow-200">
                    <p class="text-xs font-semibold text-yellow-900 mb-1">Warnings:</p>
                    ${warnings.map((warn: string) => `
                        <p class="text-xs text-yellow-700 ml-4">• ${warn}</p>
                    `).join('')}
                </div>
            `;
        }
    }

    // Add provenance note
    detailsHtml += `
        <div class="mt-3 pt-2 border-t border-gray-200">
            <p class="text-xs text-gray-500 italic">
                ✓ This is REAL discovery using live infrastructure checks - not mock data
            </p>
        </div>
    `;

    detailsDiv.innerHTML = detailsHtml;
}

// Global function for real discovery workflow
window.runDCMSDiscovery = async function(_targetInput: string | null = null): Promise<any> {
    const logger = (window as any).discoveryLogger || (window as any).logDiscovery;

    console.log('🔍 Starting REAL DCMS Discovery workflow...');

    if (!logger) {
        console.error('❌ Discovery logger not available');
        alert('Discovery logger not initialized. Please refresh the page.');
        return;
    }

    // Show the discovery log container
    logger.show();

    // Show "Preparing Discovery..." toast
    const toastId = (window as any).toastNotifications.info(
        'Preparing discovery environment...',
        {
            title: 'Preparing Discovery',
            autoHide: false  // Don't auto-hide
        }
    );

    // Store toast ID globally for later hiding
    (window as any).preparingDiscoveryToastId = toastId;

    // Disable discovery button and add visual feedback
    const btn = document.getElementById('start-dcms-discovery-btn');
    if (btn) {
        btn.setAttribute('disabled', 'true');
        btn.classList.add('discovering');
        // Update button text
        const btnText = btn.querySelector('.btn-text');
        if (btnText) {
            (btnText as HTMLElement).textContent = 'Discovering...';
        }
    }

    // Clear previous logs
    logger.clearLog();
    logger.logInfo('═══════════════════════════════════════════════════════');
    logger.logInfo('  Real DCMS Discovery - Automated Workflow');
    logger.logInfo('═══════════════════════════════════════════════════════');

    try {
        // On mainnet, skip proxy-based pre-checks (proxy not available)
        // Discovery uses direct HTTPS calls to unboundata.com
        if (environment.isMainnet) {
            logger.logInfo('🌐 Running on ICP Mainnet - using direct HTTPS discovery');
            logger.logInfo('   Discovery Hub: https://unboundata.com');
            logger.logSpacer();

            // Display mainnet-specific status panel
            displayPreCheckStatus({
                success: true,
                data: {
                    timestamp: new Date().toISOString(),
                    checks: {
                        proxy: { status: 'N/A (mainnet)', port: 'N/A' },
                        ec2: { status: 'running', region: 'ap-southeast-2' },
                        discovery_hub: { status: 'reachable', url: 'https://unboundata.com' }
                    }
                }
            });
        } else {
            // Step 1: Run pre-discovery checks (local mode only)
        logger.logInfo('STEP 1/4: Running pre-discovery system checks...');
        logger.logProgress('Checking proxy server, EC2 instances, and Discovery Hub...');

        const { discoveryPreCheck } = await import('../api/proxy-services');
        const preCheckResult = await discoveryPreCheck();

        // Display pre-check results in the status window
        displayPreCheckStatus(preCheckResult);

        if (!preCheckResult.success) {
            logger.logError('❌ ❌ Pre-discovery checks failed!');
            if (preCheckResult.data) {
                const checks = preCheckResult.data.checks;
                if (checks) {
                    logger.logError(`❌   Proxy: ${checks.proxy?.status || 'unknown'}`);
                    logger.logError(`❌   EC2: ${checks.ec2?.status || 'unknown'}`);
                    logger.logError(`❌   Discovery Hub: ${checks.discovery_hub?.status || 'unknown'}`);
                }

                // Display error messages if available
                if (preCheckResult.data.errors && preCheckResult.data.errors.length > 0) {
                    logger.logError('❌ ');
                    logger.logError('❌ Errors detected:');
                    preCheckResult.data.errors.forEach((err: string) => {
                        if (err && err.trim()) {
                            logger.logError(`❌   • ${err}`);
                        }
                    });
                }

                // Display warnings if available
                if (preCheckResult.data.warnings && preCheckResult.data.warnings.length > 0) {
                    logger.logError('❌ ');
                    logger.logError('❌ Warnings:');
                    preCheckResult.data.warnings.forEach((warn: string) => {
                        if (warn && warn.trim()) {
                            logger.logError(`❌   • ${warn}`);
                        }
                    });
                }
            }
            logger.logError('❌ Please resolve issues and try again.');
            logger.logError('❌ See "System Pre-Check Status" panel above for detailed information.');
            return { success: false, error: 'Pre-checks failed' };
        }

        logger.logSuccess('✓ Pre-discovery checks passed!');
        if (preCheckResult.data?.checks) {
            const checks = preCheckResult.data.checks;
            logger.logDetail(`  ✓ Proxy server: ${checks.proxy?.status} (port ${checks.proxy?.port || 5001})`);
            logger.logDetail(`  ✓ EC2 instances: ${checks.ec2?.status} (${checks.ec2?.region || 'ap-southeast-2'})`);
            logger.logDetail(`  ✓ Discovery Hub: ${checks.discovery_hub?.status} at ${checks.discovery_hub?.url || 'http://unboundata.com:8080'}`);
        }
        logger.logInfo('  See "System Pre-Check Status" panel for detailed provenance information.');
        logger.logSpacer();
        } // End of local-only pre-checks

        // Step 2: Start real discovery
        logger.logInfo('▶ STEP 2/4: Starting real discovery workflow...');

        // Get and validate target input
        const targetInputElement = document.getElementById('target-range-input') as HTMLInputElement;
        const targetInput = targetInputElement?.value?.trim();

        // OPTION A: Require target input
        if (!targetInput) {
            logger.logError('❌ Please enter target IP(s) in the "Target Range / IPs" field');
            logger.logError('   Examples: 192.168.1.20 or 192.168.1.0/24 or 192.168.1.10-192.168.1.30');
            return { success: false, error: 'No target IPs specified' };
        }

        // Validate IP format using ValidationUtils
        if (!ValidationUtils.isValidIPInput(targetInput)) {
            logger.logError('❌ Invalid IP format in "Target Range / IPs" field');
            logger.logError('   Supported formats:');
            logger.logError('   • Single IP: 192.168.1.20');
            logger.logError('   • CIDR: 192.168.1.0/24');
            logger.logError('   • Range: 192.168.1.10-192.168.1.30');
            logger.logError('   • Multiple: 192.168.1.20,192.168.1.25,192.168.1.30');
            return { success: false, error: 'Invalid IP format' };
        }

        // Parse and expand target input (inline implementation)
        let targetIPs: string[] = [];
        let discoveryType: 'single' | 'cidr' | 'range' | 'multiple' = 'single';

        if (targetInput.includes(',')) {
            // Comma-separated IPs
            targetIPs = targetInput.split(',').map(ip => ip.trim());
            discoveryType = 'multiple';
        } else if (targetInput.includes('-')) {
            // IP range - use endpoints for now
            const [startIP, endIP] = targetInput.split('-').map(ip => ip.trim());
            targetIPs = [startIP, endIP];
            discoveryType = 'range';
        } else if (targetInput.includes('/')) {
            // CIDR - expand to usable host IPs (skips network and broadcast addresses)
            targetIPs = expandCIDR(targetInput);
            discoveryType = 'cidr';
            logger.logInfo(`📊 Expanded CIDR ${targetInput} to ${targetIPs.length} usable host IPs`);
        } else {
            // Single IP
            targetIPs = [targetInput];
            discoveryType = 'single';
        }

        logger.logInfo(`📊 Parsed ${targetIPs.length} target IP(s) from input (${discoveryType})`);
        logger.logInfo(`🎯 Starting batch discovery for ${targetIPs.length} devices`);
        logger.logDetail(`📊 Processing in batches of 10 devices...`);

        // Use old batching code for nice UI feedback
        const logCallback = (msg: string, type: string) => logger.log(msg, type as any);
        const discoveryResult = await window.canisterClient.discoverRangeViaDirectHub(targetIPs, logCallback);

        if (!discoveryResult.success) {
            logger.logError('❌ Discovery failed!');
            logger.logError(`Error: ${discoveryResult.error || 'Unknown error'}`);
            return { success: false, error: discoveryResult.error };
        }

        logger.logInfo('');
        logger.logSuccess(`🎉 Batch discovery completed: ${discoveryResult.data?.filter((r: any) => DiscoveryStatusClassifier.isSuccessful(r.status)).length}/${targetIPs.length} devices discovered`);
        logger.logInfo(`📊 Discovery type: ${discoveryType}`);

        // Process config files for all discovered devices
        if (discoveryResult.data && Array.isArray(discoveryResult.data)) {
            // Create DCMSDiscovery instance for config processing
            const discovery = new DCMSDiscovery();
            discovery.setDependencies(logger, window.canisterClient);

            for (const result of discoveryResult.data) {
                if (DiscoveryStatusClassifier.isSuccessful(result.status)) {
                    // Store device via Storage Manager (ONE call replaces multiple calls)
                    await discovery.storeDeviceViaStorageManager(result);

                    // Log config files count if available
                    if (result.inventory?.discovered_configs) {
                        const configs = result.inventory.discovered_configs;
                        if (Array.isArray(configs) && configs.length > 0) {
                            logger.logInfo(`📁 ${result.ip}: ${configs.length} config files stored`);
                        }
                    }
                }
            }
        }

        logger.logInfo('');
        logger.logSuccess('🎉 DCMS Discovery Completed Successfully!');

        // Re-enable discovery button after completion
        const btn = document.getElementById('start-dcms-discovery-btn');
        if (btn) {
            btn.removeAttribute('disabled');
            btn.classList.remove('discovering');
            const btnText = btn.querySelector('.btn-text');
            if (btnText) {
                (btnText as HTMLElement).textContent = 'Run DCMS Discovery';
            }
        }

        return discoveryResult;

    } catch (error) {
        logger.logError(`❌ Discovery error: ${error}`);
        console.error('Discovery error:', error);

        // Re-enable discovery button after error
        const btn = document.getElementById('start-dcms-discovery-btn');
        if (btn) {
            btn.removeAttribute('disabled');
            btn.classList.remove('discovering');
            const btnText = btn.querySelector('.btn-text');
            if (btnText) {
                (btnText as HTMLElement).textContent = 'Run DCMS Discovery';
            }
        }

        return { success: false, error: String(error) };
    }
};

// Export for module system
